import { requirePermission } from "@/lib/casl/route-guards";
import { WordCloudPage } from "@/components/word-cloud";
import { useWordCloud } from "@/lib/api/wordCloud";
import { createFileRoute, useRouter, useSearch } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/_authenticated/word-cloud/")({
  component: WordCloud,
  beforeLoad: ({ context }) => {
    requirePermission(context.user, "read", "admin-analytics");
  },
});

function WordCloud() {
  const router = useRouter();
  const { startDate, endDate, mentorId, menteeId, source, limit, view } =
    useSearch({
      strict: false,
    }) as Record<string, string>;

  const [localStartDate, setLocalStartDate] = useState<Date | undefined>(
    startDate ? new Date(startDate) : undefined,
  );
  const [localEndDate, setLocalEndDate] = useState<Date | undefined>(
    endDate ? new Date(endDate) : undefined,
  );

  const viewMode = (view as "bubble" | "cloud") || "bubble";

  const { data, isPending } = useWordCloud(
    startDate,
    endDate,
    mentorId,
    menteeId,
    (source as "all" | "mentee" | "mentor") || "all",
    limit ? parseInt(limit) : 100,
  );

  const handleFilterChange = (name: string, value: string) => {
    router.navigate({
      to: router.state.location.pathname,
      search: (prev: any) => ({
        ...prev,
        [name]: value,
      }),
    });
  };

  const handleDateChange = (type: "start" | "end", date: Date | undefined) => {
    if (type === "start") {
      setLocalStartDate(date);
      handleFilterChange("startDate", date ? date.toISOString() : "");
    } else {
      setLocalEndDate(date);
      handleFilterChange("endDate", date ? date.toISOString() : "");
    }
  };

  const handleViewChange = (newView: "bubble" | "cloud") => {
    router.navigate({
      to: router.state.location.pathname,
      search: (prev: any) => ({
        ...prev,
        view: newView,
      }),
    });
  };

  return (
    <WordCloudPage
      data={data}
      isPending={isPending}
      mentorId={mentorId || ""}
      menteeId={menteeId || ""}
      source={source || "all"}
      limit={limit ? parseInt(limit) : 100}
      startDate={localStartDate}
      endDate={localEndDate}
      viewMode={viewMode}
      handleFilterChange={handleFilterChange}
      handleDateChange={handleDateChange}
      handleViewChange={handleViewChange}
    />
  );
}
